/**
 * Nmbrs Calendar Legend
 *
 * Horizontal legend for the team leave calendar.
 * Shows leave type colors, holiday indicator, and weekend indicator.
 * Story 3.4: Team Verlofkalender
 */

/** Color mapping per Nmbrs LeaveType number. */
export const LEAVE_TYPE_COLORS: Record<number, string> = {
  1: '#2F5A7A', // Navy
  2: '#54949E', // Teal
  3: '#64748B', // Slate
  4: '#4A7A9B', // Light Navy
};

export const DEFAULT_LEAVE_COLOR = '#E2E8F0';

/**
 * Display names for leave types shown in the legend.
 * Must stay in sync with LEAVE_TYPE_MAP in the backend nmbrsLeaveTypeMap.ts.
 * Confirmed 2026-07-12 against the customer's Nmbrs Verlofoverzicht UI.
 */
export const LEAVE_TYPE_NAMES: Record<number, string> = {
  1: 'Wettelijk verlof',
  2: 'Bovenwettelijk verlof',
  3: 'Kort verzuim + Bijz.verlof',
  4: 'TvT uren',
};

interface NmbrsCalendarLegendProps {
  /** Which leave type numbers are present in the current month view. */
  presentTypes: number[];
}

export function NmbrsCalendarLegend({ presentTypes }: NmbrsCalendarLegendProps) {
  return (
    <div className="flex flex-wrap items-center gap-5 px-4 py-3 bg-white border-t border-[#E2E8F0] text-xs text-[#1E293B]">
      {/* Leave type color swatches */}
      {presentTypes.map((typeNum) => {
        const color = LEAVE_TYPE_COLORS[typeNum] ?? DEFAULT_LEAVE_COLOR;
        const name = LEAVE_TYPE_NAMES[typeNum] ?? `Type ${typeNum}`;
        return (
          <div key={typeNum} className="flex items-center gap-1.5">
            <span
              className="inline-block w-3 h-3 rounded-sm flex-shrink-0"
              style={{ backgroundColor: color }}
            />
            <span>{name}</span>
          </div>
        );
      })}

      {/* Divider (only if leave types are present) */}
      {presentTypes.length > 0 && (
        <span className="text-[#E2E8F0]">|</span>
      )}

      {/* Weekend indicator */}
      <div className="flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm bg-[#F3F4F6] border border-[#E2E8F0] flex-shrink-0" />
        <span className="text-gray-500">Weekend</span>
      </div>

      {/* Holiday indicator — uses approved teal top-border, no unapproved yellow */}
      <div className="flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm bg-[#EFF6FF] border border-[#54949E] flex-shrink-0" />
        <span className="text-gray-500">Feestdag</span>
      </div>
    </div>
  );
}
